'use client';

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, Cell, XAxis, YAxis, Rectangle } from 'recharts';
import { ChevronDown, ChevronUp } from 'lucide-react';

import { ChartContainer as ShadcnChartContainer, ChartTooltip } from '@/components/ui/chart';
import { Button } from '@/components/ui/button';
import { EmptyState } from './EmptyState';
import { formatBucketDate } from '../utils/formatBucketDate';

// Custom bar shape that applies radius only to the topmost segment with a non-zero value
function createStackedBarShape(errorCodes: string[]) {
    return function StackedBarShape(props: any) {
        const { x, y, width, height, fill, payload, dataKey } = props;

        // Find the topmost error code with a non-zero value for this data point
        let topmostCode: string | null = null;
        for (let i = errorCodes.length - 1; i >= 0; i--) {
            const code = errorCodes[i];
            if (payload[code] && payload[code] > 0) {
                topmostCode = code;
                break;
            }
        }

        // Apply radius only if this bar is the topmost one
        const isTopmost = dataKey === topmostCode;
        const radius: [number, number, number, number] = isTopmost ? [4, 4, 0, 0] : [0, 0, 0, 0];

        return <Rectangle x={x} y={y} width={width} height={height} fill={fill} radius={radius} />;
    };
}

type TransactionErrorData = { errorCode: string; count: number };
type TransactionErrorsTimeSeriesEntry = { [errorCode: string]: number };

export type TransactionErrorsViewMode = 'timeSeries' | 'aggregated';

interface TransactionErrorsChartProps {
    data: TransactionErrorData[] | undefined;
    timeSeriesData?: { data: Record<string, TransactionErrorsTimeSeriesEntry>; errorCodes: string[] };
    className?: string;
    viewMode?: TransactionErrorsViewMode;
    granularity?: 'daily' | 'weekly' | 'monthly';
}

// Helper to get week start date (Monday)
function getWeekStart(date: Date): string {
    const d = new Date(date);
    const day = d.getUTCDay();
    const diff = d.getUTCDate() - day + (day === 0 ? -6 : 1);
    d.setUTCDate(diff);
    return d.toISOString().split('T')[0];
}

// Helper to get month start date
function getMonthStart(date: Date): string {
    return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-01`;
}

const DEFAULT_VISIBLE_COUNT = 7;

const CHART_COLORS = [
    'var(--chart-1)',
    'var(--chart-2)',
    'var(--chart-3)',
    'var(--chart-4)',
    'var(--chart-5)',
];

function getErrorColor(index: number): string {
    return CHART_COLORS[index % CHART_COLORS.length];
}

// Map error codes to human-readable labels
const errorCodeLabels: Record<string, string> = {
    'do_not_honor': 'Do Not Honor',
    'fraudulent_card': 'Fraudulent Card',
    'card_declined': 'Card Declined',
    'insufficient_funds': 'Insufficient Funds',
    'processing_error': 'Processing Error',
    'invalid_number': 'Invalid Number',
    'generic_decline': 'Generic Decline',
    'invalid_cvc': 'Invalid CVC',
    'stolen_card': 'Stolen Card',
    'expired_card': 'Expired Card',
    'over_limit': 'Over Limit',
    'lost_card': 'Lost Card',
    'incorrect_number': 'Incorrect Number',
    'pick_up_card': 'Pick Up Card',
    'call_issuer': 'Call Issuer',
    'PAYMENT_DECLINED': 'Payment Declined',
    'incorrect_cvc': 'Incorrect CVC',
    'authentication_required': 'Auth Required',
    'incorrect_pin': 'Incorrect PIN',
    'config_error': 'Config Error',
    'unknown': 'Unknown',
};

function getErrorLabel(code: string): string {
    return errorCodeLabels[code] || code.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

export function TransactionErrorsChart({ data, timeSeriesData, className = '', viewMode = 'timeSeries', granularity = 'daily' }: TransactionErrorsChartProps) {
    const { t } = useTranslation('analytics');
    const [isExpanded, setIsExpanded] = useState(false);

    const allAggregatedData = React.useMemo(() => {
        if (!data || data.length === 0) return [];
        return data.map(item => ({
            name: getErrorLabel(item.errorCode),
            code: item.errorCode,
            value: item.count
        }));
    }, [data]);

    const timeSeriesChartData = React.useMemo(() => {
        if (!timeSeriesData || !timeSeriesData.data) return [];

        // Get raw daily data
        const rawData = Object.entries(timeSeriesData.data)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([date, errors]) => {
                const entry: any = { date };
                Object.entries(errors).forEach(([errorCode, count]) => {
                    entry[errorCode] = count;
                });
                return entry;
            });

        // If daily, return as-is
        if (granularity === 'daily') return rawData;

        // Group by granularity
        const grouped: Record<string, any[]> = {};
        rawData.forEach(entry => {
            const d = new Date(entry.date);
            const key = granularity === 'weekly' ? getWeekStart(d) : getMonthStart(d);
            if (!grouped[key]) grouped[key] = [];
            grouped[key].push(entry);
        });

        // Aggregate grouped data
        const errorCodes = timeSeriesData.errorCodes || [];
        return Object.entries(grouped)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([date, entries]) => {
                const aggregated: any = { date };
                errorCodes.forEach(code => {
                    aggregated[code] = entries.reduce((sum, e) => sum + (e[code] || 0), 0);
                });
                return aggregated;
            });
    }, [timeSeriesData, granularity]);

    const errorCodes = timeSeriesData?.errorCodes || [];

    const hasMore = allAggregatedData.length > DEFAULT_VISIBLE_COUNT;
    const aggregatedChartData = isExpanded ? allAggregatedData : allAggregatedData.slice(0, DEFAULT_VISIBLE_COUNT);
    const hiddenCount = allAggregatedData.length - DEFAULT_VISIBLE_COUNT;

    const formatXAxisValue = (value: number) => {
        if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
        if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
        return value.toString();
    };

    const labelFormatter = (label: string) => {
        try {
            return formatBucketDate(label, { month: 'short', day: 'numeric' });
        } catch {
            return label;
        }
    };

    const AggregatedCustomTooltip = ({ active, payload }: any) => {
        if (!active || !payload || payload.length === 0) return null;
        const item = payload[0].payload;
        return (
            <div className="bg-card p-3 shadow-lg rounded border border-border">
                <p className="text-card-foreground font-medium mb-2">{item.name}</p>
                <div className="space-y-1">
                    <div className="flex items-center justify-between gap-4">
                        <span className="text-sm text-muted-foreground">{t('charts.errorCode')}:</span>
                        <span className="text-sm font-mono text-card-foreground">{item.code}</span>
                    </div>
                    <div className="flex items-center justify-between gap-4">
                        <span className="text-sm text-muted-foreground">{t('charts.count')}:</span>
                        <span className="text-sm font-medium text-card-foreground">{item.value.toLocaleString()}</span>
                    </div>
                </div>
            </div>
        );
    };

    const TimeSeriesCustomTooltip = ({ active, payload, label }: any) => {
        if (!active || !payload || payload.length === 0) return null;
        const total = payload.reduce((sum: number, entry: any) => sum + (Number(entry.value) || 0), 0);
        const reversed = [...payload].reverse();

        return (
            <div className="bg-card p-3 shadow-lg rounded border border-border">
                <p className="text-card-foreground font-medium mb-2">{labelFormatter(String(label))}</p>
                <div className="space-y-1">
                    {reversed.map((entry: any, idx: number) => {
                        const value = Number(entry.value) || 0;
                        const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';

                        return (
                            <div key={idx} className="flex items-center justify-between gap-4">
                                <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 rounded" style={{ backgroundColor: entry.color }} />
                                    <span className="text-sm text-muted-foreground">{getErrorLabel(entry.dataKey)}:</span>
                                </div>
                                <div className="text-right">
                                    <span className="text-sm font-medium text-card-foreground">{value.toLocaleString()}</span>
                                    <span className="text-xs text-muted-foreground ml-2">({percentage}%)</span>
                                </div>
                            </div>
                        );
                    })}
                    <div className="pt-2 mt-2 border-t border-border">
                        <div className="flex items-center justify-between gap-4">
                            <span className="text-sm font-medium text-muted-foreground">{t('charts.total')}:</span>
                            <span className="text-sm font-semibold text-card-foreground">{total.toLocaleString()}</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Time series view
    if (viewMode === 'timeSeries') {
        if (!timeSeriesChartData.length) {
            return <EmptyState message={t('charts.noErrors')} className={className} />;
        }

        const config: Record<string, { label: string; color: string }> = {};
        errorCodes.forEach((code, index) => {
            config[code] = { label: getErrorLabel(code), color: getErrorColor(index) };
        });

        return (
            <div className={`h-full ${className}`}>
                <ShadcnChartContainer config={config} className="w-full h-full">
                    <BarChart accessibilityLayer data={timeSeriesChartData} margin={{ top: 20, right: 30, left: 20, bottom: 40 }}>
                        <XAxis dataKey="date" tickLine={false} axisLine={false} tickMargin={8} tickFormatter={labelFormatter} />
                        <YAxis tickFormatter={formatXAxisValue} />
                        <ChartTooltip cursor={false} content={<TimeSeriesCustomTooltip />} position={{ y: 220 }} />
                        {errorCodes.map((code, index) => (
                            <Bar
                                key={code}
                                dataKey={code}
                                name={getErrorLabel(code)}
                                stackId="errors"
                                fill={getErrorColor(index)}
                                shape={createStackedBarShape(errorCodes)}
                            />
                        ))}
                    </BarChart>
                </ShadcnChartContainer>
            </div>
        );
    }

    // Aggregated view (horizontal bar chart with different colors)
    if (!allAggregatedData.length) {
        return <EmptyState message={t('charts.noErrors')} className={className} />;
    }

    const chartHeight = Math.max(216, aggregatedChartData.length * 36);

    const config: Record<string, { label: string; color: string }> = {};
    aggregatedChartData.forEach((item, index) => {
        config[item.code] = { label: item.name, color: getErrorColor(index) };
    });

    return (
        <div className={`w-full ${className}`}>
            <div style={{ height: chartHeight }}>
                <ShadcnChartContainer config={config} className="w-full h-full">
                    <BarChart
                        data={aggregatedChartData}
                        layout="vertical"
                        margin={{ top: 5, right: 30, left: 0, bottom: 5 }}
                    >
                        <XAxis type="number" tickFormatter={formatXAxisValue} />
                        <YAxis
                            type="category"
                            dataKey="name"
                            width={140}
                            tick={{ fontSize: 12 }}
                            tickLine={false}
                            axisLine={false}
                        />
                        <ChartTooltip cursor={false} content={<AggregatedCustomTooltip />} />
                        <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                            {aggregatedChartData.map((entry, index) => (
                                <Cell key={entry.code} fill={getErrorColor(index)} />
                            ))}
                        </Bar>
                    </BarChart>
                </ShadcnChartContainer>
            </div>
            {hasMore && viewMode === 'aggregated' && (
                <div className="mt-2 flex justify-center">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setIsExpanded(!isExpanded)}
                        className="text-muted-foreground hover:text-foreground"
                    >
                        {isExpanded ? (
                            <>
                                <ChevronUp className="mr-1 h-4 w-4" />
                                {t('charts.showLess')}
                            </>
                        ) : (
                            <>
                                <ChevronDown className="mr-1 h-4 w-4" />
                                {t('charts.showMore', { count: hiddenCount })}
                            </>
                        )}
                    </Button>
                </div>
            )}
        </div>
    );
}
